
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref
} from "vue";
import EntitySelectorCliente from "../../components/cliente/EntitySelectorCliente.vue";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import { useRntrcFaturamentoService } from "../../modules/business/rntrc/rntrcFaturamentoBusiness";
import {
  RntrcFaturamentoFilterPayload,
  RntrcFaturamentoResumoPayload,
  STATUS_ITEM_FATURAMENTO_RNTRC,
  TIPOS_SERVICO_RNTRC,
  formatMoeda,
  getStatusItemCor,
  getStatusItemLabel,
  getTipoServicoLabel,
  toCompetencia
} from "../../modules/business/rntrc/rntrcTypes";

interface RntrcPrestacaoContasState {
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
  competencia: Date | null;
  filter: RntrcFaturamentoFilterPayload;
  resumo: Array<RntrcFaturamentoResumoPayload>;
  buscou: boolean;
}

export default defineComponent({
  setup() {
    const hoje = new Date();
    const state = reactive<RntrcPrestacaoContasState>({
      stateReady: false,
      loadings: [],
      // fechamento é feito no mês seguinte: abre no mês anterior
      competencia: new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1),
      filter: {},
      resumo: [],
      buscou: false
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const filtroAtual = (): RntrcFaturamentoFilterPayload => ({
      ...state.filter,
      competencia: toCompetencia(state.competencia)
    });

    const submitSearch = () => {
      if (!state.competencia) {
        return;
      }
      const { data, loading, resumo } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      resumo(filtroAtual())
        .then(() => {
          const linhas: Array<RntrcFaturamentoResumoPayload> = Array.isArray(
            data.value
          )
            ? data.value
            : [];
          state.resumo = linhas.sort((a, b) =>
            (a.nomeCliente ?? "").localeCompare(b.nomeCliente ?? "")
          );
          state.buscou = true;
          removeLoading(loading);
        })
        .catch(() => removeLoading(loading));
    };

    const downloadXlsx = () => {
      if (!state.competencia) {
        return;
      }
      const { loading, downloadXlsx } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      downloadXlsx(filtroAtual())
        .then(() => removeLoading(loading))
        .catch(() => removeLoading(loading));
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : null;
    };

    const valorUnitario = (r: RntrcFaturamentoResumoPayload) =>
      r.quantidade ? (r.valorTotal ?? 0) / r.quantidade : null;

    const totalCliente = (idCliente?: number | null) =>
      state.resumo
        .filter((r) => r.idCliente === idCliente)
        .map((r) => r.valorTotal ?? 0)
        .reduce((a, b) => a + b, 0);

    const totalGeral = computed(() =>
      state.resumo.map((r) => r.valorTotal ?? 0).reduce((a, b) => a + b, 0)
    );

    onMounted(() => {
      state.stateReady = true;
      submitSearch();
    });

    return {
      stateLoading,
      tiposDisponiveis: TIPOS_SERVICO_RNTRC,
      statusDisponiveis: STATUS_ITEM_FATURAMENTO_RNTRC,
      submitSearch,
      downloadXlsx,
      confirmSelectionCliente,
      valorUnitario,
      totalCliente,
      totalGeral,
      formatMoeda,
      getStatusItemCor,
      getStatusItemLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorCliente
  }
});
